import { tva } from "@gluestack-ui/utils/nativewind-utils";

import colors from "@/config/colors";
import { Position } from "@/generated/graphql";

/**
 *
 */
interface IStatusBadgeResponse {
  color: string;
  containerClassName: string;
  textClassName: string;
  action: "muted" | "error" | "warning" | "success" | "info" | undefined;
}

/**
 * Formats player position(s) and team name into a single display string
 * @param positions - Array of player positions with abbreviations (e.g., [{abbreviation: "GK"}])
 * @param teamName - The name of the player's team, or undefined if no team
 * @param isLoading - Whether data is currently loading (affects fallback text display)
 * @returns Formatted string like "GK, CB · Team Name" or just team name if no positions
 */
export function getPositionAndTeamName(
  positions: Position[],
  teamName: string | undefined,
  isLoading: boolean
): string {
  const positionsText = positions
    ?.map(position => position.abbreviation)
    .filter(abbreviation => !!abbreviation)
    .join(", ");

  // NOTE: Use || because when teamName = "", teamNameText doesn't return 'No Team'
  const teamNameText = isLoading ? "" : (teamName ?? "No Team");

  return positionsText
    ? `${positionsText} \u00B7 ${teamNameText}`
    : teamNameText;
}

const containerSuccessStyle = tva({
  base: "justify-center border-statusBadge-success-400/30 bg-statusBadge-success-500/30",
});
const textSuccessStyle = tva({
  base: "text-statusBadge-success-500",
});

const containerWarningStyle = tva({
  base: "justify-center border-statusBadge-warning-400/30 bg-statusBadge-warning-500/30",
});
const textWarningStyle = tva({
  base: "text-statusBadge-warning-500",
});

const containerErrorStyle = tva({
  base: "justify-center border-statusBadge-error-400/30 bg-statusBadge-error-500/30",
});
const textErrorStyle = tva({
  base: "text-statusBadge-error-500",
});

const getInfoValueByCondition = (
  value: number,
  from: number,
  to: number
): IStatusBadgeResponse => {
  if (value > to) {
    return {
      color: colors.statusBadge.success[500],
      action: "success",
      containerClassName: containerSuccessStyle({}),
      textClassName: textSuccessStyle({}),
    };
  }
  if (value >= from && value < to) {
    return {
      color: colors.statusBadge.warning[500],
      action: "warning",
      containerClassName: containerWarningStyle({}),
      textClassName: textWarningStyle({}),
    };
  }
  return {
    color: colors.statusBadge.error[500],
    action: "error",
    containerClassName: containerErrorStyle({}),
    textClassName: textErrorStyle({}),
  };
};

/**
 * Returns status badge styling based on metric type and value thresholds
 * @param typeOfMetric - The type of metric being displayed ("fitScore", "gpr", or "categories")
 * @param value - The numeric value to evaluate against thresholds (null/undefined returns no badge)
 * @returns Status badge style object with color and className, or undefined if value is invalid
 */
export function getStatusBadge(
  typeOfMetric: "fitScore" | "gpr" | "categories",
  value?: number | null
): IStatusBadgeResponse | undefined {
  if (value === undefined || value === null || typeof value !== "number")
    return undefined;

  switch (typeOfMetric) {
    case "fitScore":
      return getInfoValueByCondition(value, 0.6, 0.9);
    case "gpr":
      return getInfoValueByCondition(value, 0.1, 0.9);
    case "categories":
      return getInfoValueByCondition(value, 10, 90);
  }
}

const thresholds = [
  {
    min: -Infinity,
    max: -0.9,
    className:
      "border-statusBadge-warning-600/30 bg-statusBadge-warning-600/30",
    textClassName: "text-statusBadge-warning-600",
  },
  {
    min: -0.9,
    max: -0.75,
    className:
      "border-statusBadge-warning-500/30 bg-statusBadge-warning-500/30",
    textClassName: "text-statusBadge-warning-500",
  },
  {
    min: -0.75,
    max: -0.6,
    className:
      "border-statusBadge-warning-400/30 bg-statusBadge-warning-400/30",
    textClassName: "text-statusBadge-warning-400",
  },
  {
    min: -0.6,
    max: -0.45,
    className:
      "border-statusBadge-warning-300/30 bg-statusBadge-warning-300/30",
    textClassName: "text-statusBadge-warning-300",
  },
  {
    min: -0.45,
    max: -0.3,
    className:
      "border-statusBadge-warning-200/30 bg-statusBadge-warning-200/30",
    textClassName: "text-statusBadge-warning-200",
  },
  {
    min: -0.3,
    max: -0.15,
    className:
      "border-statusBadge-warning-100/30 bg-statusBadge-warning-100/30",
    textClassName: "text-statusBadge-warning-100",
  },
  {
    min: -0.15,
    max: 0,
    className: "border-statusBadge-warning-50/30 bg-statusBadge-warning-50/30",
    textClassName: "text-statusBadge-warning-50",
  },
  {
    min: 0,
    max: 0.15,
    className: "border-statusBadge-success-50/30 bg-statusBadge-success-50/30",
    textClassName: "text-statusBadge-success-50",
  },
  {
    min: 0.15,
    max: 0.3,
    className:
      "border-statusBadge-success-100/30 bg-statusBadge-success-100/30",
    textClassName: "text-statusBadge-success-100",
  },
  {
    min: 0.3,
    max: 0.45,
    className:
      "border-statusBadge-success-200/30 bg-statusBadge-success-200/30",
    textClassName: "text-statusBadge-success-200",
  },
  {
    min: 0.45,
    max: 0.6,
    className:
      "border-statusBadge-success-300/30 bg-statusBadge-success-300/30",
    textClassName: "text-statusBadge-success-300",
  },
  {
    min: 0.6,
    max: 0.75,
    className:
      "border-statusBadge-success-400/30 bg-statusBadge-success-400/30",
    textClassName: "text-statusBadge-success-400",
  },
  {
    min: 0.75,
    max: 0.9,
    className:
      "border-statusBadge-success-500/30 bg-statusBadge-success-500/30",
    textClassName: "text-statusBadge-success-500",
  },
  {
    min: 0.9,
    max: Infinity,
    className:
      "border-statusBadge-success-600/30 bg-statusBadge-success-600/30",
    textClassName: "text-statusBadge-success-600",
  },
];

export const getStatusBadgeTimeDecayedSeasonalGpm = (value: number) => {
  return thresholds.find(
    threshold => value >= threshold.min && value < threshold.max
  ); // Handle a default return if needed
};
